import React, { useState } from "react";

const BmiCalculator = () => {
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [bmi, setBmi] = useState(null);
  const [category, setCategory] = useState("");
  const [idealWeight, setIdealWeight] = useState("");

  const calculateBMI = () => {
    if (!feet || !weight || feet <= 0 || weight <= 0) return;

    const totalInches = Number(feet) * 12 + Number(inches || 0);
    const heightInMeters = totalInches * 0.0254;

    const bmiValue = weight / (heightInMeters * heightInMeters);
    const roundedBmi = bmiValue.toFixed(1);
    setBmi(roundedBmi);

    if (bmiValue < 18.5) {
      setCategory({ text: "Underweight", emoji: "😐", color: "#09bbaf" });
    } else if (bmiValue < 25) {
      setCategory({ text: "Normal weight", emoji: "✅", color: "#ffcb24" });
    } else if (bmiValue < 30) {
      setCategory({ text: "Overweight", emoji: "⚠️", color: "#f39c12" });
    } else {
      setCategory({ text: "Obesity", emoji: "❌", color: "#e74c3c" });
    }

    calculateIdealWeight(heightInMeters);
  };

  const calculateIdealWeight = (heightInMeters) => {
    const minWeight = (18.5 * heightInMeters * heightInMeters).toFixed(1);
    const maxWeight = (24.9 * heightInMeters * heightInMeters).toFixed(1);
    setIdealWeight(`${minWeight} - ${maxWeight} kg`);
  };

  const resetForm = () => {
    setFeet("");
    setInches("");
    setWeight("");
    setAge("");
    setGender("");
    setBmi(null);
    setCategory("");
    setIdealWeight("");
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        padding: "100px 20px",
        background: "radial-gradient(circle at 50% 20%, #e7f2ff 0%, #ffffff 70%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "1200px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "60px",
          flexWrap: "wrap",
        }}
      >
        {/* LEFT SECTION - INFORMATION */}
        <div style={{ flex: "1 1 50%", minWidth: "300px" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
              borderRadius: "20px",
              padding: "40px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <h1
              style={{
                fontSize: "3rem",
                fontWeight: "800",
                color: "#0b1c39",
                marginBottom: "20px",
                lineHeight: "1.2",
              }}
            >
              BMI Calculator <span style={{ color: "#ffcb24" }}>for Everyone</span>
            </h1>
            <p
              style={{
                fontSize: "1.1rem",
                color: "#555",
                lineHeight: "1.6",
                marginBottom: "30px",
              }}
            >
              Your BMI (Body Mass Index) is calculated using your height and weight. 
              Maintaining a healthy BMI reduces the risk of chronic diseases.
            </p>

            {/* BMI Result Display */}
            {bmi && (
              <div
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  borderRadius: "20px",
                  padding: "30px",
                  marginTop: "30px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <h2
                  style={{
                    color: "#0b1c39",
                    fontSize: "1.5rem",
                    marginBottom: "15px",
                    fontWeight: "700",
                  }}
                >
                  Your Results
                </h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "15px",
                    marginBottom: "15px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "2.5rem",
                      fontWeight: "800",
                      color: "#ffcb24",
                    }}
                  >
                    {bmi}
                  </div>
                  <div
                    style={{
                      padding: "10px 20px",
                      borderRadius: "25px",
                      backgroundColor: category.color,
                      color: "#0b1c39",
                      fontSize: "1rem",
                      fontWeight: "700",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {category.emoji} {category.text}
                  </div>
                </div>
                {idealWeight && (
                  <p
                    style={{
                      color: "#555",
                      fontSize: "1rem",
                      marginTop: "10px",
                    }}
                  >
                    Healthy weight range: {idealWeight}
                  </p>
                )}
              </div>
            )}

            {/* BMI Scale */}
            <div
              style={{
                marginTop: "30px",
                background: "rgba(255, 255, 255, 0.1)",
                borderRadius: "20px",
                padding: "25px",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <h3
                style={{
                  color: "#0b1c39",
                  fontSize: "1.25rem",
                  marginBottom: "15px",
                  fontWeight: "700",
                }}
              >
                BMI Categories:
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {[ 
                  { range: "Underweight", value: "< 18.5", color: "#09bbaf" },
                  { range: "Normal weight", value: "18.5 - 24.9", color: "#ffcb24" },
                  { range: "Overweight", value: "25 - 29.9", color: "#f39c12" },
                  { range: "Obesity", value: "≥ 30", color: "#e74c3c" },
                ].map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 15px",
                      borderRadius: "20px",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <span style={{ color: "#0b1c39", fontSize: "1rem" }}>
                      {item.range}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <span style={{ color: "#555", fontSize: "1rem" }}>{item.value}</span>
                      <div
                        style={{
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          backgroundColor: item.color,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION - FORM */}
        <div
          style={{
            flex: "20 1 40%",
            minWidth: "300px",
            padding: "40px",
            borderRadius: "25px",
            backgroundColor: "#ffffff",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          }}
        >
          <h2
            style={{
              color: "#0b1c39",
              fontSize: "2rem",
              fontWeight: "800",
              marginBottom: "25px",
            }}
          >
            Calculate Your BMI
          </h2>

          {/* Height Inputs */}
          <label
            style={{
              display: "block",
              color: "#0b1c39",
              fontSize: "1rem",
              marginBottom: "8px",
              fontWeight: "700",
            }}
          >
            Height
          </label>
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
            <input
              type="number"
              value={feet}
              onChange={(e) => setFeet(e.target.value)}
              placeholder="Feet"
              min="1"
              style={{
                flex: 1,
                padding: "16px",
                borderRadius: "15px",
                border: "2px solid #09bbaf",
                fontSize: "1rem",
                color: "#0b1c39",
              }}
            />
            <input
              type="number"
              value={inches}
              onChange={(e) => setInches(e.target.value)}
              placeholder="Inches"
              min="0"
              style={{
                flex: 1,
                padding: "16px",
                borderRadius: "15px",
                border: "2px solid #09bbaf",
                fontSize: "1rem",
                color: "#0b1c39",
              }}
            />
          </div>

          {/* Weight Input */}
          <label
            style={{
              display: "block",
              color: "#0b1c39",
              fontSize: "1rem",
              marginBottom: "8px",
              fontWeight: "700",
            }}
          >
            Weight (kg)
          </label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="Enter your weight in kilograms"
            style={{
              width: "100%",
              padding: "16px",
              marginBottom: "20px",
              borderRadius: "15px",
              border: "2px solid #09bbaf",
              fontSize: "1rem",
              color: "#0b1c39",
            }}
            min="1"
          />

          {/* Age & Gender */}
          <div style={{ display: "flex", gap: "15px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  color: "#0b1c39",
                  fontSize: "1rem",
                  marginBottom: "8px",
                  fontWeight: "700",
                }}
              >
                Age
              </label>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="Age"
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "15px",
                  border: "2px solid #09bbaf",
                  fontSize: "1rem",
                  color: "#0b1c39",
                }}
                min="1"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: "block",
                  color: "#0b1c39",
                  fontSize: "1rem",
                  marginBottom: "8px",
                  fontWeight: "700",
                }}
              >
                Gender
              </label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "15px",
                  border: "2px solid #09bbaf",
                  backgroundColor: "#ffffff",
                  fontSize: "1rem",
                  color: "#0b1c39",
                }}
              >
                <option value="">Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "15px" }}>
            <button
              onClick={calculateBMI}
              disabled={!feet || !weight}
              style={{
                flex: 2,
                padding: "16px",
                backgroundColor: !feet || !weight ? "#bdc3c7" : "#ffcb24",
                color: "#0b1c39",
                fontSize: "1.1rem",
                fontWeight: "700",
                border: "none",
                borderRadius: "15px",
                cursor: !feet || !weight ? "not-allowed" : "pointer",
                transition: "all 0.3s ease",
              }}
            >
              Calculate BMI
            </button>
            <button
              onClick={resetForm}
              style={{
                flex: 1,
                padding: "16px",
                backgroundColor: "#09bbaf",
                color: "#fff",
                fontSize: "1.1rem",
                fontWeight: "700",
                border: "none",
                borderRadius: "15px",
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BmiCalculator;
